import { getCaptchaToken } from "@/features/utils/captcha";
import { useControlledEffect } from "@/features/utils/use-controlled-effect";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { AuthAPI } from "../api/auth";
import type { OtherFactor } from "../types";
import { userKeys } from "./use-user";
import { useTranslation } from "@/features/utils/i18n";

export function useAddSecondaryEmail() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean },
    Error,
    { emailChallengeToken: string }
  >({
    mutationFn: AuthAPI.addSecondaryEmail,
    onSuccess: () => {
      // Invalidate user queries to refresh the user data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success(t("security.messages.secondaryEmailAdded"));
    },
  });
}

export function useRemoveSecondaryEmail() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { email: string }>({
    mutationFn: AuthAPI.removeSecondaryEmail,
    onSuccess: () => {
      // Invalidate user queries to refresh the user data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success(t("security.messages.secondaryEmailRemoved"));
    },
  });
}

export function useMakePrimaryEmail() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { email: string }>({
    mutationFn: AuthAPI.makePrimaryEmail,
    onSuccess: () => {
      // Invalidate user queries to refresh the user data
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success(t("security.messages.primaryEmailUpdated"));
    },
  });
}

export function useEmailChallengeFlow(
  options: {
    enforcedValue?: {
      label: string; // Ex: "r****m@wanadoo.fr"
      id: string; // Used by the backend to find the correct email
    };
    onSuccess?: (
      preSignedToken: string,
      otherFactors: OtherFactor[],
      email: string,
      isNewEmail: boolean
    ) => void;
    retryDelaySeconds?: number; // Default: 60 seconds
  } = {}
) {
  const { enforcedValue, onSuccess, retryDelaySeconds = 60 } = options;
  const { t } = useTranslation();

  // Raw hooks using AuthAPI methods directly
  const sendChallengeRaw = useMutation({
    mutationFn: AuthAPI.emailChallenge,
  });
  const verifyRaw = useMutation({
    mutationFn: AuthAPI.emailVerify,
  });

  // State
  const [email, setEmail] = useState("");
  const [codeSent, setCodeSent] = useState(enforcedValue?.id ? true : false);
  const [retryTimeLeft, setRetryTimeLeft] = useState(0);
  const [isNewEmail, setIsNewEmail] = useState(false);

  // Start retry timer
  const startRetryTimer = useCallback(() => {
    setRetryTimeLeft(retryDelaySeconds);
    const interval = setInterval(() => {
      setRetryTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [retryDelaySeconds]);

  // Send challenge
  const sendChallenge = useCallback(async () => {
    try {
      const emailToUse = enforcedValue?.id || email;
      const result = await sendChallengeRaw.mutateAsync({
        email: emailToUse,
        captchaToken: await getCaptchaToken("email_challenge"),
      });

      if (!result.success) {
        throw new Error(result.message || t("security.messages.challengeFailed"));
      }

      if (result.otherFactors.length === 0) {
        setIsNewEmail(true);
      } else {
        setIsNewEmail(false);
      }

      setCodeSent(true);
      startRetryTimer();
      toast.success(t("security.messages.codeSentEmail"));
    } catch (error) {
      console.error(error);
      const errorMessage =
        error instanceof Error ? error.message : t("security.messages.challengeFailed");
      toast.error(errorMessage);
      throw error;
    }
  }, [email, enforcedValue, sendChallengeRaw, startRetryTimer]);

  // Verify code
  const verifyCode = useCallback(
    async (otp: string) => {
      try {
        const emailToUse = enforcedValue?.id || email;
        const result = await verifyRaw.mutateAsync({
          email: emailToUse,
          otp,
        });

        if (result.success) {
          toast.success(t("security.messages.emailVerified"));
          // Check if this is a new email (no other factors means new user)
          const isNew = (result.otherFactors || []).length === 0;
          onSuccess?.(
            result.preSignedToken,
            result.otherFactors || [],
            emailToUse,
            isNew
          );
        } else {
          throw new Error(result.message || t("security.messages.verifyFailed"));
        }
      } catch (error) {
        console.error(error);
        const errorMessage =
          error instanceof Error ? error.message : t("security.messages.verifyFailed");
        toast.error(errorMessage);
        throw error;
      }
    },
    [email, enforcedValue, verifyRaw, onSuccess]
  );

  // Retry (go back to email input if not enforced, or resend if enforced)
  const retry = useCallback(() => {
    if (enforcedValue) {
      // If enforced, just resend the challenge
      sendChallenge();
    } else {
      // If not enforced, go back to email input
      setCodeSent(false);
      setRetryTimeLeft(0);
    }
  }, [enforcedValue, sendChallenge]);

  // Auto-send challenge if enforced value is provided
  useControlledEffect(() => {
    if (enforcedValue?.id) {
      sendChallenge();
    }
  }, [enforcedValue?.id]);

  return {
    email,
    setEmail,
    sendChallenge,
    codeSent,
    verifyCode,
    isVerifyingCode: verifyRaw.isPending,
    retryTimeLeft,
    canRetry: retryTimeLeft === 0,
    retry,
    isNewEmail,
  };
}
